import { css, html, LitElement, type PropertyValues } from "lit";
import { type PixelIconName, renderPixelIcon } from "../shared/pixel-icons";

export type AppDockItem = {
	activity?: "playing";
	active?: boolean;
	attention?: boolean;
	icon?: PixelIconName;
	id: string;
	label: string;
	title?: string;
};

export class AppDock extends LitElement {
	static properties = {
		canScrollBackward: { state: true },
		canScrollForward: { state: true },
		hasOverflow: { state: true },
		items: { attribute: false },
	};

	static styles = css`
		:host {
		display: block;
		inline-size: 100%;
		padding-inline:
			calc(env(safe-area-inset-left, 0px) + 8px)
			calc(env(safe-area-inset-right, 0px) + 8px);
		padding-block-end: env(safe-area-inset-bottom, 0px);
		box-sizing: border-box;
		font-family: var(--z-dock-font, "Courier New", monospace);
		pointer-events: none;
	}

	.dock-nav {
		display: flex;
		align-items: stretch;
		justify-content: center;
		max-inline-size: 100%;
		pointer-events: auto;
	}

	.dock-scroll {
		display: flex;
		min-inline-size: 0;
		max-inline-size: 100%;
		overflow-x: auto;
		overflow-y: hidden;
		scroll-behavior: smooth;
		scroll-snap-type: x proximity;
		scrollbar-width: none;
	}

	.dock-scroll::-webkit-scrollbar {
		display: none;
	}

	.dock {
		display: flex;
		flex: 0 0 auto;
		min-inline-size: max-content;
		margin: 0;
		padding: 0;
		list-style: none;
		color: #171411;
		background: #f4eee7;
		border: 1px solid #171411;
		border-block-end: 0;
		border-radius: 6px 6px 0 0;
		overflow: hidden;
		box-shadow: 2px 2px 0 rgb(23 20 17 / 18%);
	}

	.dock-item + .dock-item {
		border-inline-start: 1px solid #171411;
	}

	.dock-item {
		scroll-snap-align: start;
	}

	.app-button {
		position: relative;
		display: grid;
		grid-template-rows: 40px auto;
		place-items: center;
		gap: 3px;
		inline-size: 84px;
		min-block-size: 76px;
		padding: 8px 7px 7px;
		color: inherit;
		background: #f4eee7;
		border: 0;
		font: 400 11px/1.1 var(--z-dock-font, "Courier New", monospace);
		cursor: pointer;
	}

	.app-button:hover {
		background: #fffaf4;
	}

	.app-button:active,
	.app-button[aria-pressed="true"] {
		background: #d8eef0;
	}

	.app-button[aria-pressed="true"] .label {
		padding: 1px 3px;
		color: #ffffff;
		background: #171411;
	}

	.app-button:focus-visible,
	.dock-page:focus-visible {
		outline: 1px dotted #171411;
		outline-offset: -5px;
	}

	.dock-page {
		display: grid;
		place-items: center;
		flex: 0 0 28px;
		inline-size: 28px;
		min-block-size: 76px;
		padding: 0;
		color: #171411;
		background: #f4eee7;
		border: 1px solid #171411;
		border-block-end: 0;
		font: 700 12px/1 var(--z-dock-font, "Courier New", monospace);
		cursor: pointer;
	}

	.dock-page[hidden] {
		display: none;
	}

	.dock-page:disabled {
		color: #77716a;
		cursor: default;
	}

	.dock-page-back {
		border-inline-end: 0;
		border-radius: 6px 0 0 0;
	}

	.dock-page-forward {
		border-inline-start: 0;
		border-radius: 0 6px 0 0;
	}

	.dock-nav[data-overflow="true"] .dock {
		border-radius: 0;
	}

	.icon-wrap {
		position: relative;
		display: grid;
		place-items: center;
		inline-size: 40px;
		block-size: 40px;
	}

	.pixel-icon {
		inline-size: 32px;
		block-size: 32px;
		image-rendering: pixelated;
		shape-rendering: crispEdges;
	}

	.playback-meter {
		position: absolute;
		inset-inline-end: 0;
		inset-block-end: 3px;
		display: flex;
		align-items: end;
		gap: 1px;
		inline-size: 13px;
		block-size: 12px;
		padding: 2px;
		box-sizing: border-box;
		background: #101810;
		border: 1px solid #f4eee7;
		box-shadow: 0 0 0 1px #171411;
	}

	.playback-meter > span {
		inline-size: 2px;
		block-size: 7px;
		background: #66d35f;
		transform: scaleY(0.35);
		transform-origin: center bottom;
		animation: radio-level 620ms steps(3, end) infinite alternate;
	}

	.playback-meter > span:nth-child(2) {
		block-size: 9px;
		animation-duration: 470ms;
		animation-delay: -180ms;
	}

	.playback-meter > span:nth-child(3) {
		block-size: 6px;
		animation-duration: 760ms;
		animation-delay: -390ms;
	}

	.attention-dot {
		position: absolute;
		inset-block-start: 1px;
		inset-inline-end: 1px;
		inline-size: 6px;
		block-size: 6px;
		background: #dc3d45;
		border: 1px solid #171411;
		border-radius: 50%;
	}

	.label {
		max-inline-size: 100%;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	@keyframes radio-level {
		0%,
		15% {
			transform: scaleY(0.35);
		}

		50% {
			transform: scaleY(1);
		}

		75% {
			transform: scaleY(0.6);
		}

		100% {
			transform: scaleY(0.85);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.dock-scroll {
			scroll-behavior: auto;
		}

		.playback-meter > span {
			animation: none;
			transform: scaleY(0.7);
		}
	}

	@media (max-width: 359px) {
		:host {
			padding-inline: 4px;
		}

		.dock-scroll {
			justify-content: flex-start;
		}

		.app-button {
			inline-size: 82px;
		}
	}
	`;

	canScrollBackward = false;

	canScrollForward = false;

	hasOverflow = false;

	items: AppDockItem[] = [];

	#measureFrame: number | null = null;

	#resizeObserver: ResizeObserver | null = null;

	connectedCallback(): void {
		super.connectedCallback();
		window.addEventListener("resize", this.#scheduleOverflowMeasurement, {
			passive: true,
		});
	}

	disconnectedCallback(): void {
		window.removeEventListener("resize", this.#scheduleOverflowMeasurement);
		this.#resizeObserver?.disconnect();
		this.#resizeObserver = null;
		if (this.#measureFrame !== null)
			window.cancelAnimationFrame(this.#measureFrame);
		this.#measureFrame = null;
		super.disconnectedCallback();
	}

	protected firstUpdated(): void {
		const scroller = this.#scroller();
		if (scroller && "ResizeObserver" in window) {
			this.#resizeObserver = new ResizeObserver(
				this.#scheduleOverflowMeasurement,
			);
			this.#resizeObserver.observe(scroller);
			const dock = this.renderRoot.querySelector<HTMLElement>(".dock");
			if (dock) this.#resizeObserver.observe(dock);
		}
		this.#scheduleOverflowMeasurement();
	}

	protected updated(changedProperties: PropertyValues): void {
		if (changedProperties.has("items")) this.#scheduleOverflowMeasurement();
	}

	render() {
		return html`
			<nav class="dock-nav" aria-label="Applications" data-overflow=${this.hasOverflow}>
				<button
					class="dock-page dock-page-back"
					type="button"
					aria-label="Previous applications"
					?hidden=${!this.hasOverflow}
					?disabled=${!this.canScrollBackward}
					@click=${() => this.#pageDock(-1)}
				>◀</button>
				<div class="dock-scroll" @scroll=${this.#scheduleOverflowMeasurement}>
				<ul class="dock">
					${this.items.map(
						(item) => html`
							<li class="dock-item">
								<button
									class="app-button"
									type="button"
									data-app-id=${item.id}
									data-activity=${item.activity ?? "idle"}
									aria-pressed=${item.active ? "true" : "false"}
									aria-label=${item.title ?? item.label}
									title=${item.title ?? item.label}
									@click=${() => this.#activate(item.id)}
									@keydown=${this.#handleDockKeyDown}
								>
									<span class="icon-wrap" aria-hidden="true">
										${renderPixelIcon(item.icon ?? "window", "")}
										${
											item.activity === "playing"
												? html`
													<span class="playback-meter">
														<span></span><span></span><span></span>
													</span>
												`
												: null
										}
										${item.attention ? html`<span class="attention-dot"></span>` : null}
									</span>
									<span class="label">${item.label}</span>
								</button>
							</li>
					`,
					)}
				</ul>
				</div>
				<button
					class="dock-page dock-page-forward"
					type="button"
					aria-label="Next applications"
					?hidden=${!this.hasOverflow}
					?disabled=${!this.canScrollForward}
					@click=${() => this.#pageDock(1)}
				>▶</button>
			</nav>
		`;
	}

	#scroller(): HTMLElement | null {
		return this.renderRoot.querySelector<HTMLElement>(".dock-scroll");
	}

	#scheduleOverflowMeasurement = () => {
		if (this.#measureFrame !== null) return;
		this.#measureFrame = window.requestAnimationFrame(() => {
			this.#measureFrame = null;
			this.#syncOverflowState();
		});
	};

	#syncOverflowState(): void {
		const scroller = this.#scroller();
		if (!scroller) return;
		const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
		const hasOverflow = maxScroll > 1;
		const canScrollBackward = hasOverflow && scroller.scrollLeft > 1;
		const canScrollForward = hasOverflow && scroller.scrollLeft < maxScroll - 1;
		if (
			hasOverflow === this.hasOverflow &&
			canScrollBackward === this.canScrollBackward &&
			canScrollForward === this.canScrollForward
		)
			return;
		this.hasOverflow = hasOverflow;
		this.canScrollBackward = canScrollBackward;
		this.canScrollForward = canScrollForward;
	}

	#pageDock(direction: -1 | 1): void {
		const scroller = this.#scroller();
		if (!scroller) return;
		const tileWidth =
			this.renderRoot.querySelector<HTMLElement>(".app-button")?.offsetWidth ??
			84;
		scroller.scrollLeft += direction * tileWidth * 2;
		this.#scheduleOverflowMeasurement();
	}

	#handleDockKeyDown = (event: KeyboardEvent) => {
		if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
		const buttons = [
			...this.renderRoot.querySelectorAll<HTMLButtonElement>(".app-button"),
		];
		const currentIndex = buttons.indexOf(
			event.currentTarget as HTMLButtonElement,
		);
		if (currentIndex < 0 || !buttons.length) return;
		event.preventDefault();
		const nextIndex =
			event.key === "Home"
				? 0
				: event.key === "End"
					? buttons.length - 1
					: (currentIndex +
							(event.key === "ArrowRight" ? 1 : -1) +
							buttons.length) %
						buttons.length;
		buttons[nextIndex]?.focus();
		buttons[nextIndex]?.scrollIntoView?.({
			block: "nearest",
			inline: "nearest",
		});
	};

	#activate(id: string) {
		this.dispatchEvent(
			new CustomEvent("dock-item-activate", {
				bubbles: true,
				composed: true,
				detail: { itemId: id },
			}),
		);
	}
}

if (!customElements.get("app-dock")) {
	customElements.define("app-dock", AppDock);
}
